<template>
  <div class="contact-page">
    <section class="contact-section-neutral">
      <div class="container">
        <!-- Header -->
        <div class="section-header mb-5 mt-4">
          <div class="section-icon-wrap icon-indigo">
            <i class="fa-solid fa-comments"></i>
          </div>
          <div>
            <h1 class="section-title">{{ $t('publish.contactTitle') }}</h1>
            <p class="body-text mt-2" v-html="$t('publish.contactIntro')"></p>
          </div>
        </div>

        <!-- Grid -->
        <div class="libraries-grid-neutral">
          <div class="lib-card-plain" v-for="(lib, key) in libraries" :key="key">
            <div class="lib-header-plain">
               <h4 class="lib-name-plain">
                 <a :href="'https://bibliotecnica.upc.edu/' + lib.url" target="_blank">{{ lib.name }}</a>
               </h4>
            </div>
            <div class="lib-body-plain">
               <div class="lib-person">
                 <i class="fa-solid fa-user me-2"></i> {{ lib.person }}
               </div>
               <div class="lib-email">
                 <i class="fa-solid fa-envelope me-2"></i> 
                 <a :href="'mailto:' + lib.email">{{ lib.email }}</a>
               </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { defineComponent, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import store from "@/store";

export default defineComponent({
  name: 'ContactView',
  setup() {
    const route = useRoute();

    const libraries = {
      brgf: { name: 'Biblioteca Rector Gabriel Ferraté', email: 'montse.moragas@upc.edu', person: 'Montse Moragas', url: 'brgf' },
      bct: { name: 'Biblioteca del Campus de Terrassa', email: 'isabel.molinos@upc.edu', person: 'Isabel Molinos', url: 'bct' },
      bcbl: { name: 'Biblioteca del Campus del Baix Llobregat', email: 'gemma.garcia@upc.edu', person: 'Gemma García', url: 'bcbl' },
      bcum: { name: 'Biblioteca del Campus Universitari de Manresa', email: 'conxa.moncunill@upc.edu', person: 'Conxa Moncunill', url: 'bcum' },
      etseib: { name: 'Biblioteca de l\'Escola Tècnica Superior d\'Enginyeria Industrial de Barcelona', email: 'margarita.anglada@upc.edu', person: 'Margarita Anglada', url: 'etseib' },
      etsab: { name: 'Biblioteca Oriol Bohigas de l\'Escola Tècnica Superior d\'Arquitectura de Barcelona', email: 'fani.diaz@upc.edu', person: 'Fani Díaz', url: 'etsab' },
      eebe: { name: 'Biblioteca de l\'Escola d\'Enginyeria de Barcelona Est', email: 'marina.casadevall@upc.edu', person: 'Marina Casadevall', url: 'eebe' },
      epseb: { name: 'Biblioteca de l\'Escola Politècnica Superior d\'Edificació de Barcelona', email: 'rosalia.ordonez@upc.edu', person: 'Rosalía Ordóñez', url: 'epseb' },
      epsevg: { name: 'Biblioteca de l\'Escola Politècnica Superior d\'Enginyeria de Vilanova i la Geltrú', email: 'tais.bages@upc.edu', person: 'Taïs Bagés', url: 'epsevg' },
      etsav: { name: 'Biblioteca de l\'Escola Tècnica Superior d\'Arquitectura del Vallès', email: 'angel.beumala@upc.edu', person: 'Àngel Beumala', url: 'etsav' },
      fme: { name: 'Biblioteca de la Facultat de Matemàtiques i Estadística', email: 'sara.matheu@upc.edu', person: 'Sara Matheu', url: 'fme' },
      fnb: { name: 'Biblioteca de la Facultat de Nàutica de Barcelona', email: 'manel.mendoza@upc.edu', person: 'Manel Mendoza', url: 'fnb' }
    };

    onMounted(async () => {
      await store.dispatch('updateSearchSelected', "Tots");
      await store.dispatch('updateBackgroundImage', { 
        view: route.name, 
        url: require('@/assets/images/sections/banner-blue.webp') 
      });
    });

    return {
      libraries
    };
  }
});
</script>

<style scoped>
.contact-page {
  --text-primary: #0c2340;
  --text-secondary: #4a5568;
  --apren-500: #0a66c2;
  --bg-light: #f8fafc;
  
  font-family: 'Roboto', Helvetica, serif;
  color: var(--text-primary);
  min-height: 80vh;
}

.contact-section-neutral {
  padding: 3rem 0 5rem;
}

/* ════════════════════════════════════════════════
   SECTION HEADERS
════════════════════════════════════════════════ */
.section-header {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
}

.section-icon-wrap {
  flex-shrink: 0;
  width: 54px;
  height: 54px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}

.icon-indigo { background: #e0e7ff; color: #4f46e5; }

.section-title {
  font-size: 2.25rem;
  font-weight: 800;
  color: var(--text-primary);
  margin: 0;
  letter-spacing: -0.02em;
}

.body-text {
  font-size: 18px;
  font-weight: 300;
  line-height: 28px;
  color: var(--text-secondary);
}

/* ════════════════════════════════════════════════
   LIBRARIES GRID
════════════════════════════════════════════════ */
.libraries-grid-neutral {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2.5rem 2rem;
}

.lib-card-plain {
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.lib-name-plain { font-size: 18px; font-weight: 700; margin: 0 0 0.5rem 0; }
.lib-name-plain a { color: var(--text-primary); text-decoration: none; }
.lib-name-plain a:hover { color: var(--apren-500); }

.lib-person { font-size: 16px; font-weight: 300; color: var(--text-secondary); margin-bottom: 0.25rem; }
.lib-email { font-size: 16px; font-weight: 400; }
.lib-email a { color: var(--apren-500); text-decoration: none; }
.lib-email a:hover { text-decoration: underline; }

@media (max-width: 992px) {
  .libraries-grid-neutral { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 576px) {
  .libraries-grid-neutral { grid-template-columns: 1fr; }
  .section-header { flex-direction: column; gap: 1rem; }
}
</style>
